import React from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  FileText, 
  Building2,
  LayoutDashboard, 
  Settings, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setAuth0Instance } from '../../store/api/apiSlice';
import { setAuthenticated, setUser } from '../../store/slices/authSlice';

export const Layout: React.FC = () => {
  const auth0 = useAuth0();
  const { logout, user, isAuthenticated, getAccessTokenSilently } = auth0;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const dispatch = useDispatch();

  // Set Auth0 instance for API calls and sync with Redux
  useEffect(() => {
    setAuth0Instance(auth0);
    console.log('Auth0 instance set for API calls', {
      isAuthenticated: auth0.isAuthenticated,
      hasGetAccessTokenSilently: !!auth0.getAccessTokenSilently,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE
    });

    // Also sync Auth0 state with Redux (as backup)
    if (isAuthenticated && user) {
      const syncAuthState = async () => {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: "openid profile email"
            }
          });
          
          dispatch(setAuthenticated({ isAuthenticated: true, token }));
          dispatch(setUser({
            id: user.sub || '',
            auth0_user_id: user.sub || '',
            email: user.email || '',
            name: user.name || user.email || 'Unknown User',
            picture: user.picture,
            role: 'user',
            is_active: true,
            tenant_id: '',
            created_at: new Date().toISOString()
          }));
          
          console.log('Synced Auth0 state with Redux successfully');
        } catch (error) {
          console.error('Failed to sync Auth0 state with Redux:', error);
        }
      };
      
      syncAuthState();
    } else {
      dispatch(setAuthenticated({ isAuthenticated: false }));
      dispatch(setUser(null));
    }
  }, [auth0, isAuthenticated, user, getAccessTokenSilently, dispatch]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Vendors', href: '/vendors', icon: Building2 },
    { name: 'Documents', href: '/documents', icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-950">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-gray-800`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            {sidebarOpen && (
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"></div>
                  <h1 className="text-lg font-bold tracking-tight">
                    <span className="text-gray-900 dark:text-gray-100">Contract</span>
                    <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent ml-1">
                      AI
                    </span>
                  </h1>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? 'nav-link-active' : ''} ${
                      !sidebarOpen ? 'justify-center' : ''
                    }`
                  }
                >
                  <Icon size={20} />
                  {sidebarOpen && <span className="ml-3">{item.name}</span>}
                </NavLink>
              );
            })}
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center space-x-3">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name || 'User'}
                  className="w-8 h-8 rounded-full"
                />
              )}
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
              )}
            </div>
            {sidebarOpen && (
              <button
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="w-full mt-3 nav-link text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut size={20} />
                <span className="ml-3">Sign out</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Welcome back
            </h2>
            <div className="flex items-center space-x-4">
              {/* Add notifications, search, etc. here */}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}; 
